import {
  DAY_SECONDS,
  FIXED_TIMESTEP,
  INITIAL_CABLES,
  INITIAL_GRID_HEIGHT,
  INITIAL_GRID_WIDTH,
  INITIAL_RELAYS,
  INITIAL_SWITCHES,
  PERMANENT_UPGRADES,
  PULSE_BASE_SPEED,
  PULSE_ENERGY,
  type SimSpeed,
} from '../config/balance.js';
import type { ColorId } from '../config/colors.js';
import { createRng, restoreRng, type Rng } from './rng.js';
import {
  canPlaceBuilding,
  cellKey,
  clearObstaclesUnderNodes,
  createInitialTerrain,
  defaultPortOffset,
  expandGridSymmetric,
  findFreeAnchorsForKind,
  gridDimsForDay,
  pickBuildingAnchor,
  shiftCableCellsAfterExpand,
  shiftNodesAfterExpand,
} from './grid.js';
import {
  buildCrowdedPulseCells,
  buildNetworkAnalysis,
  buildingConnectionCells,
  edgeEffectiveThroughput,
  edgeKeyFromCells,
  enterCableAtCell,
  findCellPathToNode,
  isTeleportRouteId,
  orientCableFromCell,
  parseTeleportRoute,
  pathToCableRoute,
  pulseIsMeeting,
  segmentSpeedMult,
  type NetworkAnalysis,
} from './network.js';
import {
  validateCablePath,
  eraseCableFromState,
  eraseCableCellFromState,
} from './cables.js';
import {
  createRouteCache,
  buildPulseRoute,
  type RouteCache,
} from './routing.js';
import {
  deliverPulseToNode,
  tickBulbConsumption,
  tickGenerators,
} from './energy.js';
import { runSpawnTick, nextSpawnDelay } from './spawn.js';
import { applyRewardOffer, generateWeeklyRewards } from './rewards.js';
import { tickScoreSurvival, dayFromSimTime, weekFromDay } from './score.js';
import type {
  Cable,
  CellCoord,
  GameState,
  PermanentUpgrades,
  SerializedSimulation,
  SimulationEvent,
  GameNode,
  MapId,
  RunModifiers,
} from './types.js';

export type SimulationListener = (event: SimulationEvent) => void;

function defaultRunModifiers(): RunModifiers {
  return {
    pulseSpeedMult: 1,
    bulbCapacityMult: 1,
    globalThroughputMult: 1,
    interferenceResist: 0,
    magneticResist: 0,
    edgeThroughputBonus: 0,
    generatorProductionMult: 0,
    expressLine: false,
    digitalBridge: false,
  };
}

function applyPermanentUpgrades(
  base: RunModifiers,
  upgrades: PermanentUpgrades,
  cables: { initial: number },
  relays: { initial: number },
): RunModifiers {
  const mods = { ...base };
  const cab = upgrades.optimizedCabling ?? 0;
  const rel = upgrades.relayReserve ?? 0;
  const spd = upgrades.improvedTransmission ?? 0;
  const thr = upgrades.reinforcedCircuits ?? 0;
  const bat = upgrades.improvedBatteries ?? 0;
  const mag = upgrades.magneticShielding ?? 0;
  const interf = upgrades.interferenceReduction ?? 0;

  cables.initial = Math.floor(
    INITIAL_CABLES *
      (1 + cab * PERMANENT_UPGRADES.optimizedCabling.effectPerLevel),
  );
  relays.initial = INITIAL_RELAYS + Math.floor(rel * PERMANENT_UPGRADES.relayReserve.effectPerLevel);
  mods.pulseSpeedMult += spd * PERMANENT_UPGRADES.improvedTransmission.effectPerLevel;
  mods.globalThroughputMult += thr * PERMANENT_UPGRADES.reinforcedCircuits.effectPerLevel;
  mods.bulbCapacityMult += bat * PERMANENT_UPGRADES.improvedBatteries.effectPerLevel;
  mods.magneticResist += mag * PERMANENT_UPGRADES.magneticShielding.effectPerLevel;
  mods.interferenceResist += interf * PERMANENT_UPGRADES.interferenceReduction.effectPerLevel;

  return mods;
}

export class Simulation {
  state: GameState;
  private accumulator = 0;
  private routeCache: RouteCache = createRouteCache();
  private listeners: SimulationListener[] = [];
  /**
   * Token bucket per undirected cable edge (pulses/s).
   * Refills at `edgeEffectiveThroughput`; each cell-boundary crossing costs 1.
   */
  private edgeTokens = new Map<string, number>();
  /** Edges already refilled during the current sim tick. */
  private edgeTokenRefilled = new Set<string>();
  private rng: Rng;
  private analysisCache: NetworkAnalysis | null = null;
  private analysisVersion = -1;

  constructor(state: GameState, rng: Rng) {
    this.state = state;
    this.rng = rng;
  }

  /** Cached network analysis — rebuilt only when topology changes. */
  getNetworkAnalysis(): NetworkAnalysis {
    if (
      !this.analysisCache ||
      this.analysisVersion !== this.state.networkVersion
    ) {
      this.analysisCache = buildNetworkAnalysis(this.state);
      this.analysisVersion = this.state.networkVersion;
    }
    return this.analysisCache;
  }

  on(listener: SimulationListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private emit(event: SimulationEvent): void {
    for (const l of this.listeners) l(event);
  }

  static createNewRun(
    mapId: MapId,
    permanentUpgrades: PermanentUpgrades = {},
    seed = Date.now(),
  ): Simulation {
    const rng = createRng(seed >>> 0);
    const cableBudget = { initial: INITIAL_CABLES };
    const relayBudget = { initial: INITIAL_RELAYS };
    const runModifiers = applyPermanentUpgrades(
      defaultRunModifiers(),
      permanentUpgrades,
      cableBudget,
      relayBudget,
    );

    const genId = 'n0';
    const bulbId = 'n1';
    const terrain = createInitialTerrain(
      mapId,
      INITIAL_GRID_WIDTH,
      INITIAL_GRID_HEIGHT,
      seed,
    );
    const baseCtx = {
      gridWidth: INITIAL_GRID_WIDTH,
      gridHeight: INITIAL_GRID_HEIGHT,
      terrain,
      nodes: [] as GameState['nodes'],
      cableCells: new Set<string>(),
    };
    // Prefer classic layout; relocate if an obstacle sits under the footprint.
    // Two gens (0.5/s each) for one standard bulb (0.75/s) so the run is feasible.
    const genAnchor =
      pickBuildingAnchor(baseCtx, 'generator', { x: 4, y: 3 }) ?? { x: 4, y: 3 };
    const genNode: GameState['nodes'][number] = {
      id: genId,
      kind: 'generator',
      color: 'cyan',
      anchor: genAnchor,
      generatorSize: 'small',
      productionTimer: 0,
    };
    baseCtx.nodes.push(genNode);
    // Left of the first gen — keeps the (4,3)→(9,3) cable corridor free.
    const gen2Anchor =
      pickBuildingAnchor(baseCtx, 'generator', { x: 3, y: 3 }) ?? { x: 3, y: 4 };
    const gen2Node: GameState['nodes'][number] = {
      id: 'n2',
      kind: 'generator',
      color: 'cyan',
      anchor: gen2Anchor,
      generatorSize: 'small',
      productionTimer: 0,
    };
    baseCtx.nodes.push(gen2Node);
    const bulbAnchor =
      pickBuildingAnchor(baseCtx, 'bulb', { x: 9, y: 2 }) ?? { x: 9, y: 2 };
    const bulbNode: GameState['nodes'][number] = {
      id: bulbId,
      kind: 'bulb',
      color: 'cyan',
      // 1×2 vertical; ≥1 of 5 approach cells around the port stays free
      anchor: bulbAnchor,
      portOffset: defaultPortOffset('bulb'),
      consumptionTier: 'standard',
      energy: 100 * runModifiers.bulbCapacityMult,
    };
    baseCtx.nodes.push(bulbNode);
    clearObstaclesUnderNodes(terrain, baseCtx.nodes);

    const state: GameState = {
      mapId,
      seed: seed >>> 0,
      simTime: 0,
      day: 1,
      week: 1,
      score: 0,
      pulsesDelivered: 0,
      gridWidth: INITIAL_GRID_WIDTH,
      gridHeight: INITIAL_GRID_HEIGHT,
      terrain,
      nodes: baseCtx.nodes,
      cables: [],
      pulses: [],
      cablesRemaining: cableBudget.initial,
      relaysRemaining: relayBudget.initial,
      switchesRemaining: INITIAL_SWITCHES,
      teleporterPairsRemaining: 0,
      pendingTeleporterId: null,
      runModifiers,
      permanentUpgrades,
      rng: { seed: rng.state.seed },
      spawnTimer: 120,
      pendingSpawns: [],
      paused: false,
      speed: 1,
      gameOver: false,
      waitingForReward: false,
      rewardOffers: null,
      weeksRewarded: 0,
      nextNodeId: 3,
      nextCableId: 0,
      nextPulseId: 0,
      pulseRouteCursor: {},
      networkVersion: 0,
      bulbInflowLog: [],
    };

    state.spawnTimer = nextSpawnDelay(state);
    return new Simulation(state, rng);
  }

  setSpeed(speed: SimSpeed): void {
    this.state.speed = speed;
    if (this.state.paused && speed) {
      this.state.paused = false;
    }
  }

  pause(): void {
    this.state.paused = true;
  }

  resume(): void {
    this.state.paused = false;
  }

  /**
   * Sim-seconds left in the accumulator (not yet committed to a tick).
   * Used by the renderer to extrapolate pulse positions between fixed steps.
   */
  renderExtrudeSeconds(): number {
    if (this.state.paused || this.state.gameOver || this.state.waitingForReward) {
      return 0;
    }
    return Math.max(0, Math.min(this.accumulator, FIXED_TIMESTEP));
  }

  /** Advance real-time; applies speed multiplier and fixed substeps. */
  advance(realDt: number): SimulationEvent[] {
    if (this.state.paused || this.state.gameOver || this.state.waitingForReward) {
      return [];
    }
    const simDt = realDt * this.state.speed;
    this.accumulator += simDt;
    const all: SimulationEvent[] = [];
    // Cap catch-up so a slow frame cannot spiral into dozens of sim ticks.
    let steps = 0;
    while (this.accumulator >= FIXED_TIMESTEP && steps < 10) {
      all.push(...this.tickSim(FIXED_TIMESTEP));
      this.accumulator -= FIXED_TIMESTEP;
      steps++;
    }
    if (this.accumulator > FIXED_TIMESTEP * 2) {
      this.accumulator = 0;
    }
    return all;
  }

  /** Direct simulation step (sim seconds) — used for tests and deterministic advance. */
  tickSim(dt: number): SimulationEvent[] {
    if (this.state.gameOver) return [];
    const events: SimulationEvent[] = [];

    this.state.simTime += dt;
    this.state.rng.seed = this.rng.state.seed;

    const prevDay = this.state.day;
    this.state.day = dayFromSimTime(this.state.simTime);
    this.state.week = weekFromDay(this.state.day);
    if (this.state.day !== prevDay) {
      events.push({ type: 'dayChanged', day: this.state.day });
      events.push(...this.maybeExpandGrid());
      events.push(...this.maybeWeekReward());
    }

    if (this.state.waitingForReward) return events;

    tickScoreSurvival(this.state, dt);
    events.push(...tickBulbConsumption(this.state, dt));

    tickGenerators(this.state, this.routeCache, dt, (gen, target, startCableId) => {
      this.spawnPulse(gen, target, startCableId);
    });

    events.push(...this.tickPulses(dt));
    events.push(...runSpawnTick(this.state, dt));

    return events;
  }

  /** True when neither generators nor bulbs can find a free anchor. */
  private isGridSaturated(): boolean {
    const cableCells = new Set<string>();
    for (const c of this.state.cables) {
      for (const cell of c.cells) cableCells.add(cellKey(cell));
    }
    const ctx = {
      gridWidth: this.state.gridWidth,
      gridHeight: this.state.gridHeight,
      terrain: this.state.terrain,
      nodes: this.state.nodes,
      cableCells,
    };
    return (
      findFreeAnchorsForKind(ctx, 'generator').length === 0 ||
      findFreeAnchorsForKind(ctx, 'bulb').length === 0
    );
  }

  private maybeExpandGrid(): SimulationEvent[] {
    let target = gridDimsForDay(this.state.day);
    const behindSchedule =
      target.width > this.state.gridWidth || target.height > this.state.gridHeight;
    // Packed board between schedule steps: grow +2/+2 so spawn never stalls forever.
    if (!behindSchedule) {
      if (!this.isGridSaturated()) return [];
      target = {
        width: this.state.gridWidth + 2,
        height: this.state.gridHeight + 2,
      };
    }
    const { newWidth, newHeight, terrain, offsetX, offsetY } = expandGridSymmetric(
      this.state.mapId,
      this.state.seed,
      this.state.gridWidth,
      this.state.gridHeight,
      target.width,
      target.height,
      this.state.terrain,
    );
    shiftNodesAfterExpand(this.state.nodes, offsetX, offsetY);
    for (const cable of this.state.cables) {
      shiftCableCellsAfterExpand(cable.cells, offsetX, offsetY);
    }
    this.state.gridWidth = newWidth;
    this.state.gridHeight = newHeight;
    this.state.terrain = terrain;
    clearObstaclesUnderNodes(this.state.terrain, this.state.nodes);
    this.state.networkVersion++;
    const ev: SimulationEvent = { type: 'gridExpanded', newWidth, newHeight };
    this.emit(ev);
    return [ev];
  }

  private maybeWeekReward(): SimulationEvent[] {
    if (this.state.day % 7 !== 0) return [];
    const week = this.state.day / 7;
    if (week <= this.state.weeksRewarded) return [];

    this.state.weeksRewarded = week;
    this.state.waitingForReward = true;
    this.state.paused = true;
    const offers = generateWeeklyRewards(this.rng, week, this.state.mapId);
    this.state.rewardOffers = offers;
    // Return only (no emit) — GameController handles advance() events once.
    return [{ type: 'weekReward', week, offers }];
  }

  private spawnPulse(
    generator: GameNode,
    target: GameNode,
    preferStartCableId?: string,
  ): void {
    const route = buildPulseRoute(this.state, generator, target, preferStartCableId);
    if (!route) return;

    const cable = this.state.cables.find((c) => c.id === route.startCableId);
    if (!cable || cable.cells.length < 2) return;

    const genCell = buildingConnectionCells(generator)[0]!;
    const orient = orientCableFromCell(cable, genCell);

    const pulse = {
      id: `p${this.state.nextPulseId++}`,
      color: generator.color!,
      energy: PULSE_ENERGY,
      cableId: route.startCableId,
      segmentIndex: orient.segmentIndex,
      segmentT: 0,
      forward: orient.forward,
      sourceNodeId: generator.id,
      targetNodeId: target.id,
      routeCableIds: route.routeCableIds,
      routeIndex: 0,
    };
    this.state.pulses.push(pulse);
  }

  private tickPulses(dt: number): SimulationEvent[] {
    const events: SimulationEvent[] = [];
    this.edgeTokenRefilled.clear();
    const analysis = this.getNetworkAnalysis();
    const cableById = analysis.cableById;
    // O(n) meeting map once per tick — avoids O(n²) per microstep.
    const crowded = buildCrowdedPulseCells(this.state, cableById);
    const removeIds = new Set<string>();

    for (const pulse of this.state.pulses) {
      const ev = this.advancePulse(
        pulse,
        dt,
        analysis,
        removeIds,
        crowded,
        cableById,
      );
      if (ev) events.push(ev);
    }

    if (removeIds.size > 0) {
      this.state.pulses = this.state.pulses.filter(
        (p) => !removeIds.has(p.id) && p.segmentIndex >= 0,
      );
    }

    return events;
  }

  /**
   * Consume 1 capacity token on an edge (pulses/s budget).
   * Returns false when the edge is at capacity this moment.
   */
  private consumeEdgeCapacity(
    c1: { x: number; y: number },
    c2: { x: number; y: number },
    analysis: NetworkAnalysis,
    dt: number,
  ): boolean {
    const throughput = edgeEffectiveThroughput(
      analysis,
      c1,
      c2,
      this.state.runModifiers,
    );
    const ek = edgeKeyFromCells(c1, c2);
    if (!this.edgeTokenRefilled.has(ek)) {
      // First touch this tick: refill. Unknown edges start full so a lone
      // pulse is not delayed by an empty bucket warm-up.
      const prev = this.edgeTokens.get(ek);
      const base = prev === undefined ? throughput : prev;
      this.edgeTokens.set(ek, Math.min(throughput, base + throughput * dt));
      this.edgeTokenRefilled.add(ek);
    }
    const tokens = this.edgeTokens.get(ek) ?? 0;
    if (tokens < 1) return false;
    this.edgeTokens.set(ek, tokens - 1);
    return true;
  }

  private advancePulse(
    pulse: GameState['pulses'][number],
    dt: number,
    analysis: NetworkAnalysis,
    removeIds: Set<string>,
    crowded: Set<string>,
    cableById: Map<string, import('./types.js').Cable>,
  ): SimulationEvent | null {
    let cable = cableById.get(pulse.cableId);
    if (!cable || cable.cells.length < 2) {
      removeIds.add(pulse.id);
      return null;
    }

    const forward = pulse.forward !== false;
    const a = cable.cells[pulse.segmentIndex];
    const b = cable.cells[pulse.segmentIndex + 1];
    if (!a || !b) {
      if (!this.advanceToNextCable(pulse)) {
        return this.tryDeliver(pulse, removeIds);
      }
      return null;
    }
    const c1 = forward ? a : b;
    const c2 = forward ? b : a;

    const meeting = pulseIsMeeting(this.state, pulse, crowded, cableById);
    const speed =
      PULSE_BASE_SPEED *
      segmentSpeedMult(this.state, c1, c2, analysis, meeting) *
      (this.state.runModifiers.expressLine ? 1.15 : 1);

    // Mid-segment progress is free; capacity is spent when crossing a cell boundary.
    pulse.segmentT += speed * dt;

    while (pulse.segmentT >= 1) {
      const sa = cable.cells[pulse.segmentIndex];
      const sb = cable.cells[pulse.segmentIndex + 1];
      if (!sa || !sb) break;
      const edgeA = forward ? sa : sb;
      const edgeB = forward ? sb : sa;
      if (!this.consumeEdgeCapacity(edgeA, edgeB, analysis, dt)) {
        // Wait at the end of the current segment until capacity frees up.
        pulse.segmentT = Math.min(pulse.segmentT, 1 - 1e-6);
        return null;
      }

      pulse.segmentT -= 1;
      if (forward) {
        pulse.segmentIndex++;
        if (pulse.segmentIndex >= cable.cells.length - 1) {
          if (!this.advanceToNextCable(pulse)) {
            return this.tryDeliver(pulse, removeIds);
          }
          return null;
        }
      } else {
        pulse.segmentIndex--;
        if (pulse.segmentIndex < 0) {
          if (!this.advanceToNextCable(pulse)) {
            return this.tryDeliver(pulse, removeIds);
          }
          return null;
        }
      }
    }

    return null;
  }

  private advanceToNextCable(pulse: GameState['pulses'][number]): boolean {
    pulse.routeIndex++;
    if (pulse.routeIndex >= pulse.routeCableIds.length) {
      // Route incomplete (e.g. T-junction mid-cable) — repath from here.
      return this.repathPulseFromHere(pulse);
    }
    let nextId = pulse.routeCableIds[pulse.routeIndex]!;

    // Warp through a teleporter pair, then enter the cable after the exit portal.
    let fromCell: CellCoord | null = null;
    if (isTeleportRouteId(nextId)) {
      const hop = parseTeleportRoute(nextId);
      if (!hop) return this.repathPulseFromHere(pulse);
      fromCell = hop.to;
      pulse.routeIndex++;
      if (pulse.routeIndex >= pulse.routeCableIds.length) {
        return this.repathPulseFromHere(pulse);
      }
      nextId = pulse.routeCableIds[pulse.routeIndex]!;
    }

    const next = this.state.cables.find((c) => c.id === nextId);
    if (!next || next.cells.length < 2) return this.repathPulseFromHere(pulse);

    if (!fromCell) {
      // Enter the next cable from the cell we just arrived at (exit of previous).
      const prev = this.state.cables.find((c) => c.id === pulse.cableId);
      fromCell = next.cells[0]!;
      if (prev && prev.cells.length > 0) {
        const forward = pulse.forward !== false;
        fromCell = forward
          ? prev.cells[prev.cells.length - 1]!
          : prev.cells[0]!;
      }
    }

    const target = this.state.nodes.find((n) => n.id === pulse.targetNodeId);
    const toward = target ? buildingConnectionCells(target)[0] : null;
    const orient = enterCableAtCell(next, fromCell, toward);
    pulse.cableId = nextId;
    pulse.segmentIndex = orient.segmentIndex;
    pulse.forward = orient.forward;
    pulse.segmentT = 0;
    return true;
  }

  /** Rebuild remaining route when a pulse reaches a cable end mid-network. */
  private repathPulseFromHere(pulse: GameState['pulses'][number]): boolean {
    const cable = this.state.cables.find((c) => c.id === pulse.cableId);
    const target = this.state.nodes.find((n) => n.id === pulse.targetNodeId);
    if (!cable || !target || cable.cells.length < 2) return false;

    const forward = pulse.forward !== false;
    const atCell = forward
      ? cable.cells[cable.cells.length - 1]!
      : cable.cells[0]!;

    const analysis = this.getNetworkAnalysis();
    const path = findCellPathToNode(
      this.state,
      analysis,
      atCell,
      target,
      pulse.color,
    );
    if (!path || path.length < 2) return false;

    const routeCableIds = pathToCableRoute(path, this.state.cables, this.state);
    if (!routeCableIds || routeCableIds.length === 0) return false;

    // Skip current cable if path continues on it; otherwise switch.
    let startIdx = 0;
    if (routeCableIds[0] === pulse.cableId && routeCableIds.length > 1) {
      startIdx = 1;
    } else if (routeCableIds[0] === pulse.cableId) {
      // Still on same cable — re-orient toward target from current cell
      const toward = buildingConnectionCells(target)[0]!;
      const orient = enterCableAtCell(cable, atCell, toward);
      pulse.segmentIndex = orient.segmentIndex;
      pulse.forward = orient.forward;
      pulse.segmentT = 0;
      pulse.routeCableIds = routeCableIds;
      pulse.routeIndex = 0;
      return true;
    }

    const nextId = routeCableIds[startIdx]!;
    const next = this.state.cables.find((c) => c.id === nextId);
    if (!next || next.cells.length < 2) return false;

    const toward = buildingConnectionCells(target)[0]!;
    const orient = enterCableAtCell(next, atCell, toward);
    pulse.cableId = nextId;
    pulse.segmentIndex = orient.segmentIndex;
    pulse.forward = orient.forward;
    pulse.segmentT = 0;
    pulse.routeCableIds = routeCableIds.slice(startIdx);
    pulse.routeIndex = 0;
    return true;
  }

  private tryDeliver(
    pulse: GameState['pulses'][number],
    removeIds: Set<string>,
  ): SimulationEvent | null {
    // Only deliver when the pulse is actually at the target port.
    const cable = this.state.cables.find((c) => c.id === pulse.cableId);
    const target = this.state.nodes.find((n) => n.id === pulse.targetNodeId);
    if (cable && target) {
      const port = buildingConnectionCells(target)[0]!;
      const forward = pulse.forward !== false;
      const tip = forward
        ? cable.cells[cable.cells.length - 1]!
        : cable.cells[0]!;
      const atPort = tip.x === port.x && tip.y === port.y;
      if (!atPort) {
        // Lost mid-network — try one last repath before discarding
        if (this.repathPulseFromHere(pulse)) return null;
        removeIds.add(pulse.id);
        return null;
      }
    }

    removeIds.add(pulse.id);
    const ev = deliverPulseToNode(
      this.state,
      pulse.targetNodeId,
      pulse.color,
      pulse.energy,
    );
    if (ev && ev.type === 'pulseDelivered') {
      ev.pulseId = pulse.id;
      this.emit(ev);
      return ev;
    }
    return null;
  }

  buildCable(
    path: CellCoord[],
    startNodeId: string | null = null,
    endNodeId: string | null = null,
    excludeCableId?: string | null,
  ): { ok: boolean; error?: string; cableId?: string } {
    const result = validateCablePath(
      this.state,
      path,
      startNodeId,
      endNodeId,
      false,
      excludeCableId,
    );
    if (!result.ok || !result.cable) return { ok: false, error: result.error };

    const id = `c${this.state.nextCableId++}`;
    result.cable.id = id;
    this.state.cables.push(result.cable);
    this.state.cablesRemaining -= result.newCellsCost ?? 0;
    this.state.networkVersion++;
    this.routeCache.version = -1;
    return { ok: true, cableId: id };
  }

  /**
   * Replace (or create) a draft cable while drawing — cell-by-cell commit.
   * Pass null draftId to create; returns the cable id used.
   */
  upsertDraftCable(
    path: CellCoord[],
    startNodeId: string | null,
    endNodeId: string | null,
    draftId: string | null,
  ): { ok: boolean; cableId: string | null; error?: string } {
    // Validate while excluding the live draft so redrawing itself is allowed.
    if (path.length < 2) {
      if (draftId) this.eraseCable(draftId);
      return { ok: true, cableId: null };
    }
    const result = validateCablePath(
      this.state,
      path,
      startNodeId,
      endNodeId,
      false,
      draftId,
    );
    if (!result.ok || !result.cable) {
      return { ok: false, cableId: draftId, error: result.error };
    }
    if (draftId) this.eraseCable(draftId);
    const id = `c${this.state.nextCableId++}`;
    result.cable.id = id;
    this.state.cables.push(result.cable);
    this.state.cablesRemaining -= result.newCellsCost ?? 0;
    this.state.networkVersion++;
    this.routeCache.version = -1;
    return { ok: true, cableId: id };
  }

  eraseCable(cableId: string): boolean {
    const ok = eraseCableFromState(this.state, cableId);
    if (ok) this.routeCache.version = -1;
    return ok;
  }

  /** Erase one cell of a cable (splits / shortens; refunds unused cells). */
  eraseCableCell(cableId: string, cellIndex: number): boolean {
    const ok = eraseCableCellFromState(this.state, cableId, cellIndex);
    if (ok) this.routeCache.version = -1;
    return ok;
  }

  removeRelay(nodeId: string): boolean {
    const idx = this.state.nodes.findIndex((n) => n.id === nodeId && n.kind === 'relay');
    if (idx < 0) return false;
    this.state.nodes.splice(idx, 1);
    this.state.relaysRemaining++;
    this.state.networkVersion++;
    this.routeCache.version = -1;
    return true;
  }

  removeSwitch(nodeId: string): boolean {
    const idx = this.state.nodes.findIndex((n) => n.id === nodeId && n.kind === 'switch');
    if (idx < 0) return false;
    this.state.nodes.splice(idx, 1);
    this.state.switchesRemaining++;
    this.state.networkVersion++;
    this.routeCache.version = -1;
    return true;
  }

  /** Place a 1×1 relay — allowed directly on existing cables (cancels interference). */
  placeRelay(anchor: CellCoord): { ok: boolean; error?: string } {
    if (this.state.relaysRemaining <= 0) return { ok: false, error: 'no_relays' };
    const ctx = {
      gridWidth: this.state.gridWidth,
      gridHeight: this.state.gridHeight,
      terrain: this.state.terrain,
      nodes: this.state.nodes,
      // Empty on purpose: relays may sit on top of drawn cables.
      cableCells: new Set<string>(),
    };
    if (!canPlaceBuilding(ctx, anchor, 'relay')) {
      return { ok: false, error: 'blocked' };
    }
    const node = {
      id: `n${this.state.nextNodeId++}`,
      kind: 'relay' as const,
      anchor: { ...anchor },
    };
    this.state.nodes.push(node);
    this.state.relaysRemaining--;
    this.state.networkVersion++;
    return { ok: true };
  }

  /**
   * Place a 1×1 switch on cables — keeps crossing networks from mixing
   * (does not cancel interference slowdown; use a relay for that).
   */
  placeSwitch(anchor: CellCoord): { ok: boolean; error?: string } {
    if (this.state.switchesRemaining <= 0) return { ok: false, error: 'no_switches' };
    const ctx = {
      gridWidth: this.state.gridWidth,
      gridHeight: this.state.gridHeight,
      terrain: this.state.terrain,
      nodes: this.state.nodes,
      cableCells: new Set<string>(),
    };
    if (!canPlaceBuilding(ctx, anchor, 'switch')) {
      return { ok: false, error: 'blocked' };
    }
    this.state.nodes.push({
      id: `n${this.state.nextNodeId++}`,
      kind: 'switch',
      anchor: { ...anchor },
    });
    this.state.switchesRemaining--;
    this.state.networkVersion++;
    this.routeCache.version = -1;
    return { ok: true };
  }

  /**
   * Place teleporter portals: first click = A, second = B (linked pair).
   * Needs a free pair from rewards (`teleporterPairsRemaining`).
   */
  placeTeleporter(anchor: CellCoord): { ok: boolean; error?: string } {
    const pendingId = this.state.pendingTeleporterId;
    if (!pendingId && this.state.teleporterPairsRemaining <= 0) {
      return { ok: false, error: 'no_teleporters' };
    }

    const cableCells = new Set<string>();
    for (const c of this.state.cables) {
      for (const cell of c.cells) cableCells.add(cellKey(cell));
    }
    const ctx = {
      gridWidth: this.state.gridWidth,
      gridHeight: this.state.gridHeight,
      terrain: this.state.terrain,
      nodes: this.state.nodes,
      cableCells,
    };
    if (!canPlaceBuilding(ctx, anchor, 'teleporter')) {
      return { ok: false, error: 'blocked' };
    }

    if (!pendingId) {
      const id = `n${this.state.nextNodeId++}`;
      this.state.nodes.push({
        id,
        kind: 'teleporter',
        anchor: { ...anchor },
        teleporterSlot: 'A',
      });
      this.state.pendingTeleporterId = id;
      this.state.networkVersion++;
      return { ok: true };
    }

    const portalA = this.state.nodes.find(
      (n) => n.id === pendingId && n.kind === 'teleporter',
    );
    if (!portalA) {
      this.state.pendingTeleporterId = null;
      return { ok: false, error: 'no_pending' };
    }
    if (portalA.anchor.x === anchor.x && portalA.anchor.y === anchor.y) {
      return { ok: false, error: 'same_cell' };
    }

    const idB = `n${this.state.nextNodeId++}`;
    this.state.nodes.push({
      id: idB,
      kind: 'teleporter',
      anchor: { ...anchor },
      teleporterSlot: 'B',
      linkedNodeId: portalA.id,
    });
    portalA.linkedNodeId = idB;
    this.state.pendingTeleporterId = null;
    this.state.teleporterPairsRemaining--;
    this.state.networkVersion++;
    this.routeCache.version = -1;
    return { ok: true };
  }

  /** Remove a teleporter (and its linked twin). Refunds one pair if complete. */
  removeTeleporter(nodeId: string): boolean {
    const node = this.state.nodes.find((n) => n.id === nodeId && n.kind === 'teleporter');
    if (!node) return false;

    const partnerId = node.linkedNodeId;
    this.state.nodes = this.state.nodes.filter(
      (n) => n.id !== nodeId && n.id !== partnerId,
    );

    if (this.state.pendingTeleporterId === nodeId) {
      this.state.pendingTeleporterId = null;
      // Unfinished A — pair was never consumed.
    } else if (partnerId) {
      this.state.teleporterPairsRemaining++;
    }

    this.state.networkVersion++;
    this.routeCache.version = -1;
    return true;
  }

  chooseReward(index: number): void {
    const offers = this.state.rewardOffers;
    if (!offers || index < 0 || index >= offers.length) return;
    const offer = offers[index]!;
    const relays = { value: 0 };
    const switches = { value: 0 };
    const teleporters = { value: 0 };
    this.state.runModifiers = applyRewardOffer(
      this.state.runModifiers,
      offer,
      relays,
      teleporters,
      switches,
    );
    this.state.cablesRemaining += offer.cables;
    this.state.relaysRemaining += relays.value;
    this.state.switchesRemaining += switches.value;
    this.state.teleporterPairsRemaining += teleporters.value;
    this.state.rewardOffers = null;
    this.state.waitingForReward = false;
    this.state.paused = false;
    this.state.networkVersion++;
    this.analysisCache = null;
  }

  serialize(): SerializedSimulation {
    return {
      version: 1,
      state: JSON.parse(JSON.stringify(this.state)) as GameState,
      accumulator: this.accumulator,
    };
  }

  static deserialize(data: SerializedSimulation): Simulation {
    const state = data.state as GameState & { gridSize?: number };
    // Migrate legacy square-grid saves.
    if (
      (state.gridWidth == null || state.gridHeight == null) &&
      typeof state.gridSize === 'number'
    ) {
      state.gridWidth = state.gridSize;
      state.gridHeight = state.gridSize;
    }
    if (!state.bulbInflowLog) state.bulbInflowLog = [];
    if (!state.pulseRouteCursor) state.pulseRouteCursor = {};
    if (state.teleporterPairsRemaining == null) state.teleporterPairsRemaining = 0;
    if (state.pendingTeleporterId === undefined) state.pendingTeleporterId = null;
    if (state.switchesRemaining == null) state.switchesRemaining = INITIAL_SWITCHES;
    if (state.runModifiers && state.runModifiers.generatorProductionMult == null) {
      state.runModifiers.generatorProductionMult = 0;
    }
    const rng = restoreRng(state.rng);
    const sim = new Simulation(state, rng);
    sim.accumulator = data.accumulator;
    return sim;
  }

  /** Run sim time in one go (ignores pause/reward unless triggered). */
  simulateFor(seconds: number): SimulationEvent[] {
    const steps = Math.ceil(seconds / FIXED_TIMESTEP);
    const events: SimulationEvent[] = [];
    for (let i = 0; i < steps; i++) {
      if (this.state.paused && !this.state.waitingForReward) break;
      if (this.state.waitingForReward) break;
      events.push(...this.tickSim(FIXED_TIMESTEP));
    }
    return events;
  }
}
